'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faRegistered } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip, Typography } from 'src/components';
import { useGetCodeByNameQuery } from 'src/api/redux';
import { useGetModifiersInfiniteQuery } from 'src/api/redux/codes';

// Types
export interface CodeInfo {
  id: string;
  context_type?: string;
  modifier?: string;
  modifierDescription?: string;
}

export interface CodeGroup {
  codes: CodeInfo[];
  operator: string;
  inverse: boolean;
}

export interface BillingCodesState {
  groups: CodeGroup[];
  anyAllOperator: string;
}

// --- Tooltip Components ---

interface CodeData {
  id: string;
  code_name?: string;
  category?: string;
  description?: string;
  sub_categories?: string[];
}

const CodeTooltipContent = ({ codeData }: { codeData?: CodeData }) => {
  if (!codeData) {
    return (
      <Typography className="text-xs text-neutral-600 leading-tight">
        Loading code information...
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-1 max-h-32 overflow-hidden overflow-y-auto">
      {codeData.category && (
        <Typography className="text-sm leading-tight">
          {codeData.category}
        </Typography>
      )}
      {codeData.sub_categories && codeData.sub_categories.length > 0 && (
        <Typography className="text-xs text-neutral-600 leading-tight">
          {codeData.sub_categories.join(', ')}
        </Typography>
      )}
      <Typography className="text-xs text-neutral-600 leading-tight">
        {codeData.description || 'No description available.'}
      </Typography>
    </div>
  );
};

const CodeWithTooltip = ({
  codeId,
  children,
}: {
  codeId: string;
  children: React.ReactNode;
}) => {
  const {
    data: codeData,
    isLoading,
    error,
  } = useGetCodeByNameQuery(
    { codeIds: [codeId] },
    { skip: !codeId }
  );

  const cd: CodeData | undefined = codeData?.results?.[0] as CodeData | undefined;

  // If still loading or there's an error, just return the children without tooltip
  if (isLoading || error || !cd) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      slotProps={{
        containerClassName:
          'bg-white border border-neutral-200 shadow-xl rounded-lg',
        contentClassName: 'min-w-48 max-w-96 p-3',
      }}
      content={<CodeTooltipContent codeData={cd} />}
      position="top"
    >
      <span className="cursor-help">{children}</span>
    </Tooltip>
  );
};

const ModifierWithTooltip = ({
  modifier,
  description: providedDescription,
  children,
}: {
  modifier: string;
  description?: string;
  children: React.ReactNode;
}) => {
  // The builder stores the modifier's description when it's picked, so we can
  // show the tooltip without a round-trip. Only fall back to looking it up
  // (e.g. saved cohorts / the Discovery preview, which carry just the code)
  // when no description was passed in.
  const { data } = useGetModifiersInfiniteQuery(
    { q: modifier },
    { skip: !modifier || !!providedDescription }
  );

  const description = useMemo(() => {
    if (providedDescription) return providedDescription;
    const results = data?.pages?.flatMap((page) => page.results ?? []) ?? [];
    return results.find((mod) => mod.code === modifier)?.description;
  }, [providedDescription, data, modifier]);

  // While loading, or if no matching modifier is found, render plainly.
  if (!description) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      slotProps={{
        containerClassName:
          'bg-white border border-neutral-200 shadow-xl rounded-lg',
        contentClassName: 'min-w-48 max-w-96 p-3',
      }}
      content={
        <Typography className="text-xs text-neutral-600 leading-tight">
          {description}
        </Typography>
      }
      position="top"
    >
      <span className="cursor-help">{children}</span>
    </Tooltip>
  );
};

// --- Helper Functions ---

const procedurePrefixes = [
  'cpt',
  'hcpcs',
  'dme',
  'drg',
  'apc',
  'icd10p',
  'icdp',
];
const drugPrefixes = ['drug'];

export const isProcedureCode = (code: CodeInfo): boolean => {
  if (!code.context_type) return false;
  return procedurePrefixes.some((prefix) =>
    code.context_type.toLowerCase().startsWith(prefix)
  );
};

// There is only one type of diagnosis code
export const isDiagnosisCode = (code: CodeInfo): boolean => {
  if (!code.context_type) return false;
  const lower = code.context_type.toLowerCase();
  // Exclude procedure codes first
  if (lower.includes('icdp') || lower.includes('icd10p')) return false;
  return lower.startsWith('icd');
};

export const isDrugCode = (code: CodeInfo): boolean => {
  if (!code.context_type) return false;
  return drugPrefixes.some((prefix) =>
    code.context_type.toLowerCase().startsWith(prefix)
  );
};

// Helper to get styling classes (bold + color)
export const getCodeStyleClasses = (codeId: string): string => {
  const lowerCaseCodeId = codeId.toLowerCase();
  if (lowerCaseCodeId.startsWith('cpt')) return 'font-bold text-sky-600';
  if (lowerCaseCodeId.startsWith('hcpcs')) return 'font-bold text-magenta-400';
  if (lowerCaseCodeId.startsWith('icd') || lowerCaseCodeId.startsWith('drg'))
    return 'font-bold text-purple-600';
  if (lowerCaseCodeId.startsWith('drug')) return 'font-bold text-orange-400';
  return 'font-bold text-magenta-400';
};

// Helper to get ONLY the underline classes
export const getUnderlineClasses = (codeId: string): string => {
  const lowerCaseCodeId = codeId.toLowerCase();
  if (lowerCaseCodeId.startsWith('cpt')) return 'border-b border-sky-600';
  if (lowerCaseCodeId.startsWith('hcpcs')) return 'border-b border-magenta-400';
  if (lowerCaseCodeId.startsWith('icd') || lowerCaseCodeId.startsWith('drg'))
    return 'border-b border-purple-600';
  if (lowerCaseCodeId.startsWith('drug')) return 'border-b border-orange-400';
  return 'border-b border-magenta-400';
};

// Helper to parse code ID into prefix and suffix
interface ParsedCodeId {
  prefix: string;
  suffix: string;
  isCpt: boolean;
}

export const parseCodeId = (codeId: string): ParsedCodeId => {
  const [prefixPart = codeId, ...rest] = codeId.split('-');
  const isCpt = prefixPart.toLowerCase() === 'cpt';
  return {
    prefix: prefixPart,
    suffix: rest.length ? `-${rest.join('-')}` : '',
    isCpt: isCpt,
  };
};

// Helper to combine two ReactNodes with a separator
export const combineNodes = (
  node1: React.ReactNode,
  node2: React.ReactNode,
  separator: string | React.ReactNode
): React.ReactNode => {
  if (node1 && node2)
    return (
      <>
        {node1}
        {separator}
        {node2}
      </>
    );
  return node1 ?? node2 ?? null;
};

// Helper to build the styled list of codes
export const buildCodeListString = (
  codes: CodeInfo[],
  operator: 'and' | 'or'
): React.ReactNode => {
  if (codes.length === 0) return null;

  const styledCodes = codes.map((code) => {
    const codeId = code.id;
    const { prefix, suffix, isCpt } = parseCodeId(codeId);
    const styleClasses = getCodeStyleClasses(codeId);
    const underlineClasses = getUnderlineClasses(codeId);

    const modifierSuffix = code.modifier ? (
      <ModifierWithTooltip
        modifier={code.modifier}
        description={code.modifierDescription}
      >
        <span
          className="font-bold text-teal-600 ml-0.5"
          data-testid={`preview-modifier-${code.modifier}`}
        >
          {' + '}
          {code.modifier}
        </span>
      </ModifierWithTooltip>
    ) : null;

    // Special handling for drug codes - only show the drug name without prefix
    if (prefix.toLowerCase() === 'drug') {
      const drugName = suffix.startsWith('-') ? suffix.substring(1) : suffix;
      return (
        <CodeWithTooltip key={codeId} codeId={codeId}>
          <span className={`${styleClasses} whitespace-nowrap`}>
            <span className={underlineClasses}>{drugName}</span>
          </span>
        </CodeWithTooltip>
      );
    }

    // Normal handling for other code types. The code tooltip covers only the
    // code itself, and the modifier tooltip only the modifier, so the two
    // hover regions don't overlap.
    return (
      <span key={codeId} className="whitespace-nowrap">
        <CodeWithTooltip codeId={codeId}>
          <span className={styleClasses}>
            <span className={underlineClasses}>
              {prefix}
              {isCpt && (
                <Icon
                  icon={faRegistered}
                  style={{
                    fontSize: '0.5em',
                    verticalAlign: '0.35em',
                    marginLeft: '1px',
                    marginRight: '1px',
                    display: 'inline-block',
                  }}
                />
              )}
              {suffix}
            </span>
          </span>
        </CodeWithTooltip>
        {modifierSuffix}
      </span>
    );
  });

  if (styledCodes.length === 1) return styledCodes[0];

  const result: React.ReactNode[] = [];
  for (let i = 0; i < styledCodes.length; i++) {
    result.push(styledCodes[i]);
    if (i < styledCodes.length - 1) result.push(` ${operator} `);
  }
  return <>{result}</>;
};

// Helper function to build content for a single code type
const buildCodeTypeContent = (
  codes: CodeInfo[],
  type: 'diagnosis' | 'procedure' | 'drug',
  groupOpDisplay: 'and' | 'or',
  isExcluded: boolean,
  treatmentText: string,
  includePrefix = true
): React.ReactNode => {
  if (codes.length === 0) return null;

  const codeList = buildCodeListString(codes, groupOpDisplay);
  const excludePrefix = isExcluded ? 'not ' : '';

  if (!includePrefix) {
    return codeList;
  }

  if (type === 'diagnosis') {
    return (
      <>
        <strong>{excludePrefix}diagnosed with:</strong> {codeList}
      </>
    );
  } else if (type === 'procedure') {
    return (
      <>
        <strong>
          {excludePrefix}
          {treatmentText}:
        </strong>{' '}
        {codeList}
      </>
    );
  } else {
    // drug
    return (
      <>
        <strong>{excludePrefix}treated with:</strong> {codeList}
      </>
    );
  }
};

// Generates the full summary sentence with improved line breaks
export const generateSummaryString = (
  billingCodes: BillingCodesState,
  mode?: 'treating' | 'sending' | 'receiving'
): React.ReactNode => {
  const treatmentText = mode === 'sending' ? 'referred for' : 'treated for';
  const { groups, anyAllOperator } = billingCodes;
  const activeGroups = groups.filter((g) => g.codes.length > 0);

  if (activeGroups.length === 0) {
    return 'Not Available';
  }

  // Determine inter-group operator (between groups)
  const interGroupOp =
    anyAllOperator.toLowerCase() === 'or' ? 'or' : ('and' as 'and' | 'or');

  // Process each group separately and build complete strings with prefixes
  const groupParts: React.ReactNode[] = [];

  activeGroups.forEach((group) => {
    // Get the operator for codes within this group
    const intraGroupOp =
      group.operator.toLowerCase() === 'all'
        ? ('and' as 'and' | 'or')
        : ('or' as 'and' | 'or');

    // Separate codes by type within this group
    const diagCodes = group.codes.filter(isDiagnosisCode);
    const procCodes = group.codes.filter(isProcedureCode);
    const drugCodes = group.codes.filter(isDrugCode);

    // Build content for each code type in this group WITH prefix
    const typeParts: React.ReactNode[] = [];

    if (diagCodes.length > 0) {
      const diagContent = buildCodeTypeContent(
        diagCodes,
        'diagnosis',
        intraGroupOp,
        group.inverse,
        treatmentText,
        true // include prefix
      );
      if (diagContent) typeParts.push(diagContent);
    }

    if (procCodes.length > 0) {
      const procContent = buildCodeTypeContent(
        procCodes,
        'procedure',
        intraGroupOp,
        group.inverse,
        treatmentText,
        true // include prefix
      );
      if (procContent) typeParts.push(procContent);
    }

    if (drugCodes.length > 0) {
      const drugContent = buildCodeTypeContent(
        drugCodes,
        'drug',
        intraGroupOp,
        group.inverse,
        treatmentText,
        true // include prefix
      );
      if (drugContent) typeParts.push(drugContent);
    }

    // If this group has multiple code types, combine them with intra-group operator
    if (typeParts.length > 0) {
      if (typeParts.length === 1) {
        groupParts.push(typeParts[0]);
      } else {
        const combined = typeParts.reduce((acc, part, index) => {
          if (index === 0) return part;
          return (
            <>
              {acc} {intraGroupOp} {part}
            </>
          );
        }, null as React.ReactNode);
        groupParts.push(combined);
      }
    }
  });

  if (groupParts.length === 0) {
    return 'Preview not available for this code type combination.';
  }

  if (groupParts.length === 1) {
    return groupParts[0];
  }

  // Combine groups with the inter-group operator
  return groupParts.reduce((acc, part, index) => {
    if (index === 0) return part;
    return (
      <>
        {acc} {interGroupOp} {part}
      </>
    );
  }, null as React.ReactNode);
};
